import { Description, NoteAdd } from "@mui/icons-material";
import { Box, Button, LinearProgress } from "@mui/material";
import { useCallback, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";

import { getMeta } from "../../../lib/api";
import { RendererProps } from "../Input";

import { useFileUpload } from "./hooks/useFileUpload";

interface FileInputPreview {
  isDragActive: boolean;
  contentType?: string;
  fileName?: string;
  progress: number | undefined;
  value: string;
}

const FilePreview = ({
  contentType,
  fileName,
  url,
}: {
  url: string;
  fileName?: string;
  contentType?: string;
}) => {
  const [mediaType] = contentType?.split("/") ?? [];
  if (mediaType === "image") {
    return (
      <Box
        component="img"
        src={url}
        sx={{ maxWidth: "100%", maxHeight: "300px" }}
      />
    );
  }

  return (
    <>
      <Description
        sx={{ width: "3em", height: "3em", fill: "#F2B705", display: "block" }}
      />
      {fileName}
    </>
  );
};

const FileInputPreview = ({
  contentType,
  fileName,
  isDragActive,
  progress,
  value,
}: FileInputPreview) => {
  if (isDragActive) {
    return <p>Sleep de bestanden hierheen...</p>;
  }

  if (typeof progress !== "undefined") {
    return <LinearProgress value={progress} variant="determinate" />;
  }

  if (value) {
    const previewUrl = value.startsWith("http")
      ? value
      : getMeta("preview-template")?.replace(":signed_id", value);

    return (
      <FilePreview
        contentType={contentType}
        fileName={fileName}
        url={previewUrl}
      />
    );
  }

  return (
    <p>
      <Button startIcon={<NoteAdd />} variant="contained">
        Uploaden
      </Button>
    </p>
  );
};

const FileField = ({
  contentType: initialContentType,
  name,
  onChange,
  required,
  value,
}: RendererProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [contentType, setContentType] = useState<string | undefined>(
    initialContentType,
  );
  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const [uploadFile, progress, error] = useFileUpload();
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const [acceptedFile] = acceptedFiles;

    setContentType(acceptedFile.type);
    setFileName(acceptedFile.name);

    uploadFile(acceptedFile, (signedId) => {
      if (onChange) {
        onChange?.({ target: { name, value: signedId } });
      }
      if (inputRef.current) {
        inputRef.current.value = signedId;
      }
    });
  }, []);
  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    multiple: false,
    onDrop,
  });
  const previewValue = value ?? inputRef.current?.value;

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} name="file-input" />
      <Box
        component="input"
        name={name}
        ref={inputRef}
        required={required}
        sx={{
          width: 0,
          height: 0,
          position: "absolute",
          opacity: 0,
        }}
      />
      <FileInputPreview
        contentType={contentType}
        fileName={fileName}
        isDragActive={isDragActive}
        progress={progress}
        value={typeof previewValue === "string" ? previewValue : ""}
      />
      {error}
    </div>
  );
};

export default FileField;
